/**
 * What is happening in this conversation right now, between its header and its
 * transcript.
 *
 * Two things can be live at once and they are different things: a **call**,
 * which is a fact the conversation itself knows (`callState`), and a
 * **meeting** attached to it, which is a row with a link and a lobby. Both get
 * a line; neither is drawn when it is not running.
 *
 * ── The clock counts from `since`, and `since` is not the roster ────────────
 * `CallState.since` is the *first* seat's `joinedAt`, held while any seat
 * exists. Computing it from the current roster instead — the minimum of who is
 * here now — makes the clock jump backwards the moment whoever started the call
 * leaves. The server holds it for that reason and this bar only reads it.
 */

import { useEffect, useState } from 'react';
import { ds } from '../ds';
import { Link } from '../router';
import { useT } from '../i18n';
import { formatDuration } from '../i18n/format';
import { useStore } from '../lib/relay/store';
import { useCalls } from '../state/calls';
import { Avatar } from './Avatar';
import type { Person } from '../lib/relay/types';
import './chat/chat.css';

/** How many discs before the rest become "+N". Four is what fits beside the
 *  sentence at the narrowest width the shell draws this at. */
const FACES = 4;

/**
 * The call line itself, given everything it draws.
 *
 * Separated from the store lookup below so the clock can be rendered at a
 * known age — a bar whose only input is `Date.now()` can be looked at but not
 * checked.
 */
export function CallProgress({
  since,
  people,
  extra,
  joined,
  onJoin,
  onLeave
}: {
  since: number;
  /** The faces to draw, already capped. */
  people: Person[];
  /** How many more seats there are than faces. */
  extra: number;
  /** Whether this browser holds one of the seats. */
  joined: boolean;
  onJoin: () => void;
  onLeave: () => void;
}) {
  const { Button, Icon } = ds();
  const t = useT();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, []);

  const duration = formatDuration(Math.max(0, (now - since) / 1000));

  return (
    <div className="rl-callbar">
      <Icon name="Call" size={16} />
      <span style={{ flex: 1 }}>{t('conversation.call.inprogress', { duration })}</span>

      <span className="rl-stack">
        {people.map((person) => (
          <Avatar key={person.id} person={person} size={18} />
        ))}
        {extra > 0 && <span className="rl-more">+{extra}</span>}
      </span>

      {joined ? (
        <Button size="sm" variant="danger" onClick={onLeave}>
          {t('conversation.call.leave')}
        </Button>
      ) : (
        <Button size="sm" onClick={onJoin}>
          {t('conversation.call.join')}
        </Button>
      )}
    </div>
  );
}

export function CallBar({ conversationId }: { conversationId: string }) {
  const { Button, Icon } = ds();
  const t = useT();
  const calls = useCalls();
  const call = useStore((s) => s.callState(conversationId));
  const meetings = useStore((s) => s.meetings());
  const seated = call?.roster ?? [];
  const faces = useStore((s) => s.people(seated.slice(0, FACES).map((seat) => seat.userId)));

  const live = meetings.find((m) => m.conversationId === conversationId && m.status === 'live');

  if (!call && !live) return null;

  return (
    <>
      {call && (
        <CallProgress
          since={call.since}
          people={faces}
          extra={Math.max(0, seated.length - FACES)}
          // This browser, not this account: the roster is keyed by device, and
          // a seat held by the phone in somebody's pocket is not a call this
          // page can leave.
          joined={calls.active?.callId === call.callId}
          onJoin={() => calls.join(conversationId, call.callId)}
          onLeave={() => calls.hangUp()}
        />
      )}

      {live && (
        <div className="rl-callbar">
          <Icon name="VideoCall" size={16} />
          <span className="rl-truncate" style={{ flex: 1 }}>
            {t('conversation.meeting.live')} · {live.title}
          </span>
          <Link to={`/meetings/${live.id}`}>
            <Button size="sm">{t('conversation.meeting.join')}</Button>
          </Link>
        </div>
      )}
    </>
  );
}
